import { API_BASE_URL } from '../utils/constants';

/**
 * Helper to make authenticated API requests
 */
const authFetch = async (endpoint, token, options = {}) => {
  const headers = {
    ...options.headers,
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers,
  });

  return response;
};

/**
 * API service for MyManifest backend
 */
export const api = {
  /**
   * Validate a token by making a test request
   */
  async validateToken(token) {
    if (!token) return false;
    try {
      const response = await authFetch('/user/stats', token);
      return response.ok;
    } catch {
      return false;
    }
  },

  /**
   * Get user stats (credits, quota, etc.)
   */
  async getUserStats(token) {
    const response = await authFetch('/user/stats', token);
    if (!response.ok) {
      throw new Error('Failed to fetch user stats');
    }
    return response.json();
  },

  /**
   * Get all boards for the authenticated user
   */
  async getBoards(token) {
    const response = await authFetch('/boards', token);
    if (!response.ok) {
      throw new Error('Failed to fetch boards');
    }
    return response.json();
  },

  /**
   * Get a specific board by ID
   */
  async getBoard(token, boardId) {
    const response = await authFetch(`/boards/${boardId}`, token);
    if (!response.ok) {
      throw new Error('Failed to fetch board');
    }
    return response.json();
  },

  /**
   * Get a refreshed signed URL for an image by filename
   */
  async getImageUrl(token, filename) {
    const response = await authFetch(`/images/${encodeURIComponent(filename)}`, token);
    if (!response.ok) {
      throw new Error('Failed to fetch image URL');
    }
    return response.json();
  },

  /**
   * Save (create or update) a board
   */
  async saveBoard(token, boardData) {
    const response = await authFetch('/boards', token, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(boardData),
    });
    if (!response.ok) {
      throw new Error('Failed to save board');
    }
    return response.json();
  },

  /**
   * Get a publicly shared board by share token
   */
  async getSharedBoard(shareToken) {
    const response = await fetch(`${API_BASE_URL}/shared/${shareToken}`);
    if (!response.ok) {
      throw new Error('Board not found');
    }
    return response.json();
  },

  /**
   * Create a share link for a board
   */
  async shareBoard(token, boardId) {
    const response = await authFetch(`/boards/${boardId}/share`, token, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    const data = await response.json();
    return { ok: response.ok, data };
  },

  /**
   * Remove sharing for a board
   */
  async unshareBoard(token, boardId) {
    const response = await authFetch(`/boards/${boardId}/unshare`, token, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    const data = await response.json();
    return { ok: response.ok, data };
  },

  /**
   * Permanently delete the authenticated user's account and all their content.
   *
   * Required by App Store Review Guideline 5.1.1(v), which is why this must
   * stay an in-app action rather than a support request.
   */
  async deleteAccount(token) {
    const response = await authFetch('/account', token, {
      method: 'DELETE',
    });
    const data = await response.json().catch(() => ({}));
    return { ok: response.ok, data };
  },

  /**
   * Create a Stripe checkout session
   */
  async createCheckoutSession(token, { type, successUrl, cancelUrl, boardId, shippingAddress, platform }) {
    const body = {
      type,
      success_url: successUrl,
      cancel_url: cancelUrl,
      // Lets the server refuse digital purchases from the iOS app, which must
      // use In-App Purchase (guideline 3.1.1). Advisory, not a security check.
      platform,
    };

    if (boardId) {
      body.board_id = boardId;
    }
    if (shippingAddress) {
      body.shipping_address = shippingAddress;
    }

    const response = await authFetch('/payments/create-checkout-session', token, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new Error('Failed to create checkout session');
    }
    return response.json();
  },

  /**
   * Verify a Stripe payment session
   */
  async verifyPaymentSession(token, sessionId) {
    const response = await authFetch('/payments/verify-session', token, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ session_id: sessionId }),
    });
    const data = await response.json();
    return { ok: response.ok, data };
  },

  /**
   * Upload an image file, returns { imageUrl, filename, expiresAt }
   */
  async uploadImage(token, file) {
    const formData = new FormData();
    formData.append('file', file);
    const response = await authFetch('/upload-image', token, {
      method: 'POST',
      body: formData,
    });
    if (!response.ok) {
      throw new Error('Failed to upload image');
    }
    return response.json();
  },

  /**
   * Generate images using AI (streaming response)
   * Returns the fetch response for streaming processing
   */
  async generateImagesStream(token, formData) {
    const response = await fetch(`${API_BASE_URL}/generate-images-stream`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });
    return response;
  },
};

export default api;
